/**
 * Stroke-scoped undo (engine 46.4). One pointer-down → up = one TileStroke,
 * a per-cell-deduped set of (before, after) edits on one layer. The stack is
 * client-side and session-scoped; undoing re-applies `before` values, which
 * re-dirties the affected chunks so autosave persists the undone state (no
 * server-side history needed).
 *
 * TM1 wires Brush/Erase through this. TM2 adds Rect/Fill/Picker, which produce
 * one stroke each through the same door.
 */

import type { TileID } from '../model/constants';
import type { LayerTiles } from '../model/chunk';

interface Edit {
    cellX: number;
    cellY: number;
    before: TileID;
    after: TileID;
}

/** An in-progress stroke: accumulates deduped edits until committed. */
export class Stroke {
    private edits = new Map<string, Edit>();

    constructor(public readonly layerUuid: string) {}

    /**
     * Record that `cell` changed to `after` (its `before` captured on first
     * touch). Deduped per cell so a drag that revisits a cell keeps the
     * original `before`.
     */
    record(cellX: number, cellY: number, before: TileID, after: TileID): void {
        const key = `${cellX},${cellY}`;
        const existing = this.edits.get(key);
        if (existing) {
            existing.after = after; // keep original `before`
        } else {
            this.edits.set(key, { cellX, cellY, before, after });
        }
    }

    /** True when the stroke actually changed something. */
    get hasEdits(): boolean {
        for (const e of this.edits.values()) {
            if (e.before !== e.after) return true;
        }
        return false;
    }

    freeze(): FrozenStroke {
        return { layerUuid: this.layerUuid, edits: [...this.edits.values()] };
    }
}

export interface FrozenStroke {
    layerUuid: string;
    edits: Edit[];
}

/**
 * The per-map undo/redo stack. `apply`/`revert` take a resolver from layer uuid
 * to that layer's LayerTiles so strokes touch the right layer even as the
 * active layer changes.
 */
export class UndoStack {
    private undoStack: FrozenStroke[] = [];
    private redoStack: FrozenStroke[] = [];

    constructor(private readonly cap = 256) {}

    push(stroke: FrozenStroke): void {
        this.undoStack.push(stroke);
        if (this.undoStack.length > this.cap) this.undoStack.shift();
        this.redoStack.length = 0; // a new edit invalidates redo
    }

    canUndo(): boolean {
        return this.undoStack.length > 0;
    }

    canRedo(): boolean {
        return this.redoStack.length > 0;
    }

    /** Apply `before` values for the last stroke. Returns it, or null. */
    undo(resolve: (layerUuid: string) => LayerTiles | undefined): FrozenStroke | null {
        const stroke = this.undoStack.pop();
        if (!stroke) return null;
        const layer = resolve(stroke.layerUuid);
        if (layer) {
            for (const e of stroke.edits) layer.setTile(e.cellX, e.cellY, e.before);
        }
        this.redoStack.push(stroke);
        return stroke;
    }

    /** Re-apply `after` values for the last undone stroke. Returns it, or null. */
    redo(resolve: (layerUuid: string) => LayerTiles | undefined): FrozenStroke | null {
        const stroke = this.redoStack.pop();
        if (!stroke) return null;
        const layer = resolve(stroke.layerUuid);
        if (layer) {
            for (const e of stroke.edits) layer.setTile(e.cellX, e.cellY, e.after);
        }
        this.undoStack.push(stroke);
        return stroke;
    }

    clear(): void {
        this.undoStack.length = 0;
        this.redoStack.length = 0;
    }
}
